// Opportunity strategy runs as the API returns them (apps/server/src/strategy.mjs),
// and how the Strategy tab reads them. Pure, so it tests; strategyApi.ts calls the API.
import { formatDate, formatDateTime } from '../objects/format';
import { recordHref } from '../objects/routes';

export type RunKind = 'strategy' | 'pre_mortem' | 'playbook';
export type RunState = 'queued' | 'running' | 'done' | 'error';

export interface Citation {
  ref: string;
  kind: 'guideline' | 'opportunity' | 'quote' | 'contact' | 'meeting' | 'activity' | 'history';
  id?: string;
  label: string;
  quote?: string;
}
export interface Point {
  text: string;
  citations: Citation[];
}
export interface RunGuideline {
  ref: string;
  guideline_id: string | null;
  scope: string;
  name: string;
  statement: string;
  enforcement: string;
  weight: number | null;
}
export interface StrategyRun {
  id: string;
  kind: RunKind;
  trigger: 'manual' | 'stage_propose';
  state: RunState;
  stage: string | null;
  requested_at: string;
  completed_at: string | null;
  model: string | null;
  summary: string | null;
  output: ({ summary?: string } & Record<string, unknown>) | null;
  error: string | null;
  requested_by_name: string | null;
  guidelines: RunGuideline[];
}
export interface StrategyState {
  configured: boolean;
  model: string | null;
  strategy: StrategyRun | null;
  preMortem: StrategyRun | null;
  preMortemAtPropose: StrategyRun | null;
}

/** The strategy's sections, in the order the card shows them. */
export const STRATEGY_SECTIONS = [
  { key: 'win_themes', label: 'Win Themes' },
  { key: 'gaps', label: 'Gaps and Next Steps' },
  { key: 'risks', label: 'Risks' },
  { key: 'asks', label: 'Suggested Asks' },
] as const;

const OBJECT_OF: Partial<Record<Citation['kind'], string>> = {
  guideline: 'guidelines',
  opportunity: 'opportunities',
  quote: 'quotes',
  contact: 'contacts',
  meeting: 'meetings',
  activity: 'activities',
};

/** The record a citation opens, or null (field history has no page of its own). */
export function citationHref(citation: Citation): string | null {
  const object = OBJECT_OF[citation.kind];
  return object && citation.id ? recordHref(object, citation.id) : null;
}

/** A section's points from a run's output. */
export function pointsOf(run: StrategyRun | null, key: string): Point[] {
  const points = run?.output?.[key];
  return Array.isArray(points) ? (points as Point[]) : [];
}

/** The line under a card's title: when it ran, or what it is waiting for. */
export function runCaption(run: StrategyRun | null, configured: boolean): string {
  if (!run) return 'Not run yet';
  if (run.state === 'queued') return configured ? 'Queued' : 'Waiting for the AI service';
  if (run.state === 'running') return 'Running';
  if (run.state === 'error') return `Failed ${formatDateTime(run.completed_at ?? run.requested_at)}`;
  const at = run.completed_at ?? run.requested_at;
  if (run.kind === 'pre_mortem' && run.trigger === 'stage_propose') return `Written at Propose, ${formatDate(at)}`;
  return `Last run ${formatDateTime(at)}${run.requested_by_name ? ` by ${run.requested_by_name}` : ''}`;
}

/** Whether a run is still to come back. */
export const isPending = (run: StrategyRun | null): boolean => run?.state === 'queued' || run?.state === 'running';
